import styled from "styled-components";
import { useEffect, useState } from "react";

import { BaseComponentProps } from "../types";
import {
  ChickenNuggetsActionKeys,
  NuggetsType,
  useChickenNuggetsContext,
} from "./ChickenNuggetsContext";

import { AllIconName } from "../Icon/types";
import { Icon } from "../Icon/Icon";
import { ChickenNuggetsProvider } from "./ChickenNuggetsProvider";
import { Nugget } from "./Nugget";

type ButtonType = {
  className?: string;
  iconName: AllIconName;
  type: "dark" | "white";
  handleClick: () => void;
};

const BaseButton = ({
  className,
  iconName,
  handleClick,
}: ButtonType): JSX.Element => {
  return (
    <button className={className} onClick={handleClick}>
      <Icon iconName={iconName} iconSize="small" />
    </button>
  );
};

const PaginateButton = styled(BaseButton)`
  background: transparent;
  border: none;
  cursor: pointer;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  padding: 0;
  display: flex;
  align-items: center;
  ${(props): string =>
    props.iconName === "chevron-left" ? "left: -24px;" : "right: -24px;"}
  svg {
    ${(props) =>
      props.type === "white" && "--main-color: var(--color-neutral-000-full);"}
  }
`;

const BaseChickenNuggetsContainer = styled.div`
  display: flex;
  padding: 0.5rem 0;
  justify-content: center;
  align-items: center;
  position: relative;
  width: fit-content;
  margin: auto;
`;

type ChickenNuggetsContainerProps = BaseComponentProps<"button"> & {
  className?: string;
  id?: string;
  testId?: string;
  /** Color of nuggets rendered */
  color: "dark" | "white";
  /** Limit of nuggets to a page */
  limit: number;
};

const ChickenNuggetsContainer = ({
  className,
  id,
  testId,
  color,
  limit,
}: ChickenNuggetsContainerProps): JSX.Element => {
  const {
    nuggets,
    state: { activeIndex, startIndex, slicedNuggets },
    dispatch,
  } = useChickenNuggetsContext();
  const [pages, setPages] = useState<number[]>([]);

  // Runs once on mount by design. It pushes into `pages` in place rather than
  // replacing it, so running again would append duplicate page markers and
  // break pagination, and the dispatch below feeds state this component reads,
  // which would loop. Making it react to `nuggets` means deriving `pages`
  // instead of mutating it, which changes behaviour and belongs in its own
  // story.
  useEffect(() => {
    for (let i = 0; i < nuggets.length; i++) {
      const newPages = pages;
      if (!(i % limit)) {
        newPages.push(i);
        setPages(newPages);
      }
    }

    if (pages.length)
      dispatch({
        type: ChickenNuggetsActionKeys.SlicedNuggets,
        payload: nuggets.slice(pages[0], pages[0] + limit),
      });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const handleButton = (index: number) => {
    dispatch({
      type: ChickenNuggetsActionKeys.StartIndex,
      payload: index,
    });
    dispatch({
      type: ChickenNuggetsActionKeys.ActiveIndex,
      payload: pages[index],
    });
    dispatch({
      type: ChickenNuggetsActionKeys.SlicedNuggets,
      payload: nuggets.slice(pages[index], pages[index] + limit),
    });

    if (nuggets[pages[index]].onClick) {
      nuggets[pages[index]]?.onClick?.(nuggets[pages[index]].id);
    }
  };
  return (
    <BaseChickenNuggetsContainer
      className={className}
      data-testid={testId}
      id={id}
    >
      {startIndex !== 0 && (
        <PaginateButton
          iconName="chevron-left"
          type={color}
          handleClick={(): void => {
            const index = startIndex - 1;
            handleButton(index);
          }}
        />
      )}
      {slicedNuggets.map((nugget) => {
        const index = nuggets.findIndex((nug) => nug.id === nugget.id);
        return (
          <Nugget
            index={index}
            nugget={nugget}
            color={color}
            key={`image-${nugget.id}-${index}`}
            active={activeIndex === index}
          />
        );
      })}
      {/* Checks if it should show the "Next" button */}
      {pages.length > 1 && startIndex < pages.length - 1 && (
        <PaginateButton
          iconName="chevron-right"
          type={color}
          handleClick={(): void => {
            const index = startIndex + 1;
            handleButton(index);
          }}
        />
      )}
    </BaseChickenNuggetsContainer>
  );
};

type ChickenNuggetsProps = BaseComponentProps<"div"> & {
  className?: string;
  id?: string;
  testId?: string;
  /** Color of nuggets rendered */
  color?: "dark" | "white";
  /** Limit of nuggets to a page */
  limit?: number;
  /** Array of { id: string; onClick?: (args? any) => } */
  nuggets: NuggetsType[];
  /** Set the default active nugget with index */
  index?: number;
};

export const ChickenNuggets = ({
  className,
  id,
  testId,
  color = "dark",
  limit = 10,
  nuggets,
  index = 0,
}: ChickenNuggetsProps) => {
  return (
    <ChickenNuggetsProvider nuggets={nuggets} index={index}>
      <ChickenNuggetsContainer
        className={className}
        id={id}
        testId={testId}
        color={color}
        limit={limit}
      />
    </ChickenNuggetsProvider>
  );
};
